import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { AppLockConfiguration, CategoryAppLockConfigurationService } from '@rocksoft/settings-sdk';
import { EMPTY, Observable } from 'rxjs';
import { first, map, mergeMap } from 'rxjs/operators';
import { CategoryConfigurationInheritedApiActions } from '../category-configuration-inherited/category-configuration-inherited.actions';
import { safeMap } from '../effects.helpers';
import { LocationsRouterStoreSelectors } from '../router';
import { AppLockApiActions, CategoryAppLockViewActions } from './category-app-lock.actions';

@Injectable()
export class AppLockEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly categoryAppLockApi = inject(CategoryAppLockConfigurationService);

  /**
   * App lock has been selected for a category.
   * Effect: we add the app lock to the category via the API.
   */
  appLockSelectedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAppLockViewActions.appLockSelected),
      mergeMap((action) => {
        return this.store.select(LocationsRouterStoreSelectors.selectCategory(action.categoryType)).pipe(
          first(),
          mergeMap((category) => {
            if (!category) {
              return EMPTY;
            }

            const categoryType = category.categoryType;

            const requestPayload: AppLockConfiguration = {
              bundleIdentifier: action.bundleIdentifier,
              models: action.modelNames,
              appLockState: action.appLockState ?? 'None',
            };

            return safeMap(
              this.categoryAppLockApi.addAppLockToCategory({
                categoryType,
                categoryId: category.categoryId,
                appLockConfiguration: requestPayload,
              }),
              () =>
                CategoryConfigurationInheritedApiActions.categoryConfigurationInheritedRequested({
                  categoryId: category.categoryId,
                }),
              (error) =>
                AppLockApiActions.appLockSetError({
                  category,
                  error,
                })
            );
          })
        );
      })
    );
  });

  /**
   * App lock has been removed from a category.
   * Effect: we remove the app lock from the category via the API.
   */
  appLockRemovedFromCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAppLockViewActions.appLockRemoved),
      mergeMap((action) => {
        return this.store.select(LocationsRouterStoreSelectors.selectCategory(action.categoryType)).pipe(
          first(),
          mergeMap((category) => {
            if (!category) {
              return EMPTY;
            }

            const categoryType = category.categoryType;

            return safeMap(
              this.categoryAppLockApi.deleteAppLockFromCategory({
                categoryType,
                categoryId: category.categoryId,
                state: action.appLockState,
                models: action.modelNames,
              }),
              () =>
                CategoryConfigurationInheritedApiActions.categoryConfigurationInheritedRequested({
                  categoryId: category.categoryId,
                }),
              (error) =>
                AppLockApiActions.appLockRemovedError({
                  category,
                  error,
                })
            );
          })
        );
      })
    );
  });

  appLockUpdatedErrorEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppLockApiActions.appLockSetError, AppLockApiActions.appLockRemovedError),
      map((action) =>
        CategoryConfigurationInheritedApiActions.categoryConfigurationInheritedRequested({
          categoryId: action.category.categoryId,
        })
      )
    );
  });
}
